import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { getApiOrigin } from '../utils/assetUrls';
import { hasChatSidebarAccess } from '../utils/chatPermissions';
import {
  CHAT_MESSAGE_EVENT,
  dispatchChatMessageEvent,
  dispatchChatUnreadRefresh,
} from '../services/chatService';

const SOCKET_URL =
  typeof window !== 'undefined' ? getApiOrigin() : 'http://localhost:3000';

/**
 * Keeps chat socket alive app-wide so header notifications update outside /chat.
 */
export default function useGlobalChatSocket(user) {
  useEffect(() => {
    const userId = user?.id;
    const roleId = user?.role_id;
    if (!userId || !hasChatSidebarAccess(roleId)) {
      return undefined;
    }

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 4000,
    });

    const onConnect = () => {
      socket.emit('join-chat', userId);
    };

    const onReceive = (msg) => {
      const toMe = Number(msg?.to_user_id) === Number(userId);
      const fromMe = Number(msg?.from_user_id) === Number(userId);
      if (!toMe && !fromMe) return;

      // Inbound: header badge + ChatSystem open thread
      if (toMe) {
        dispatchChatMessageEvent(msg);
      } else {
        // Outbound echo (other tabs / list refresh) without bumping unread badge logic
        window.dispatchEvent(new CustomEvent(CHAT_MESSAGE_EVENT, { detail: msg }));
      }
      dispatchChatUnreadRefresh();
    };

    const onMessagesRead = () => {
      dispatchChatUnreadRefresh();
    };

    socket.on('connect', onConnect);
    socket.on('receive_message', onReceive);
    socket.on('messages_read', onMessagesRead);
    if (socket.connected) {
      onConnect();
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('receive_message', onReceive);
      socket.off('messages_read', onMessagesRead);
      socket.disconnect();
    };
  }, [user?.id, user?.role_id]);
}
